import { ApolloCache } from "@apollo/client";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { keyBy, pick } from "lodash";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import { Writeable } from "utils/typescriptUtilis";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import {
  DimensionInput,
  SkuComponentInput,
  SkuInput,
  TiHiInput,
} from "__generated__/gql/graphql";
import * as skuSchemaFields from "utils/schemas/sku";
import * as skuComponentSchemaFields from "utils/schemas/skuComponent";
import type { Dimensions } from "utils/schemas/dimensions";
import type { TiHi } from "utils/schemas/tiHi";
import {
  makeClearCachedType,
  makeClearNestedListFieldCache,
} from "utils/apollo/cacheHelpers";
import { assertPresence } from "utils/assertions";
import setFieldErrorsWithDict, {
  ErrorsByFieldName,
} from "components/Shared/Forms/setFieldErrorsWithDict";
import UserError from "utils/UserError";
import { DIMENSIONS_FIELDS } from "components/Shared/Forms/DimensionsFieldset";
import { TI_HI_FIELDS } from "components/Shared/Forms/TiHiFieldset";
import {
  getInitialSkuComponents,
  SKU_COMPONENT_TYPES,
  useValidateUpcUniqueness,
} from "./SkuComponents/utils";
import type { SubmitContext } from "./Form";

export const skuFields = [
  "name",
  "shortName",
  "productLineId",
  "internalSkuId",
  "unitNetWeight",
  "unitNetWeightUnit",
  "grossPalletWeightLbs",
  "unitCogs",
  "unitSrp",
] as const;

export const skuComponentFields = [
  "skuComponentType",
  "upc",
  "grossWeightLbs",
] as const;

export type SkuPrimitiveInputs = Required<
  Pick<Writeable<SkuInput>, (typeof skuFields)[number]>
>;

export type SkuComponentPrimitiveInputs = Required<
  Pick<Writeable<SkuComponentInput>, (typeof skuComponentFields)[number]>
>;

export type SkuComponentDatum = SkuComponentPrimitiveInputs & {
  dimensions?: Dimensions | null;
};

export type SkuInputs = SkuPrimitiveInputs & {
  productAttributeIds: UUID[];
  tiHi?: TiHi | null;
  skuComponents?: SkuComponentDatum[];
};

export const skuSchema: yup.ObjectSchema<SkuInputs> = yup
  .object()
  .shape({
    productLineId: skuSchemaFields.productLineId,
    name: skuSchemaFields.name,
    shortName: skuSchemaFields.shortName.defined(),
    internalSkuId: skuSchemaFields.internalSkuId,
    unitNetWeight: skuSchemaFields.unitNetWeight,
    unitNetWeightUnit: skuSchemaFields.unitNetWeightUnit,
    grossPalletWeightLbs: skuSchemaFields.grossPalletWeightLbs,
    unitCogs: skuSchemaFields.unitCogs,
    unitSrp: skuSchemaFields.unitSrp,
    productAttributeIds: yup
      .array()
      .of(yup.string().uuid().required())
      .ensure()
      .default([])
      .defined()
      .label("Certifications & Attributes"),
    tiHi: skuSchemaFields.tiHi,
    skuComponents: yup.array().of(skuComponentSchemaFields.default),
  })
  .required();

export const { FormField, ControlledFormField } =
  createFormFieldComponents<SkuInputs>({
    schema: skuSchema,
  });

export const skuComponentFieldsComponentFragment = gql(/* GraphQL */ `
  fragment SelectedSkuSkuComponentFields on SkuComponent {
    id
    skuId
    skuComponentType
    upc
    grossWeightLbs
    dimensions {
      id
      height
      width
      depth
    }
  }
`);

export const skuComponentFieldsFragment = gql(/* GraphQL */ `
  fragment SelectedSkuSkuComponentsFields on Sku {
    id
    skuComponentsList {
      id
      ...SelectedSkuSkuComponentFields
    }
  }
`);

export const fragment = gql(/* GraphQL */ `
  fragment SkuPanelEdit on Sku {
    id
    name
    shortName
    isArchived
    internalSkuId
    unitNetWeight
    unitNetWeightUnit
    grossPalletWeightLbs
    unitCogs
    unitSrp
    productLineId
    productLine {
      id
      ...SelectedSkuProductLineFields
    }
    productAttributesList(
      filter: { isArchived: { equalTo: false } }
      orderBy: NAME_ASC
    ) {
      id
      ...AttributeSelectOption
    }
    tiHi {
      id
      ti
      hi
    }
    updatedAt
    ...SelectedSkuSkuComponentsFields
    ...MutationDataSkuFields
  }
`);

export type EditSkuFragment = FragmentType<typeof fragment>;

export function getSkuFormDefaultValues(
  skuFragment: EditSkuFragment,
): SkuInputs {
  const sku = getFragmentData(fragment, skuFragment);

  const skuComponentsByType = keyBy(
    getFragmentData(
      skuComponentFieldsComponentFragment,
      getFragmentData(skuComponentFieldsFragment, sku)?.skuComponentsList,
    ),
    "skuComponentType",
  );

  const skuComponents = getInitialSkuComponents().map<SkuComponentDatum>(
    (sc) => {
      const skuComponent = skuComponentsByType?.[sc.skuComponentType];
      if (!skuComponent) return sc;
      return {
        ...sc,
        ...pick(skuComponent, skuComponentFields),
        dimensions: skuComponent.dimensions
          ? pick(skuComponent.dimensions, DIMENSIONS_FIELDS)
          : null,
      };
    },
  );

  return {
    ...pick(sku, skuFields),
    productAttributeIds:
      sku.productAttributesList?.map((attribute) => attribute.id) || [],
    skuComponents,
    tiHi: sku.tiHi ? pick(sku.tiHi, TI_HI_FIELDS) : null,
  };
}

gql(/* GraphQL */ `
  fragment UpdateSkuO2O on Mutation {
    upsertDimensionsById(input: { dimensions: $dimensions }) {
      dimensions {
        id
      }
    }
    upsertTiHisById(input: { tiHis: $tiHis }) {
      tiHis {
        id
      }
    }
  }
`);

gql(/* GraphQL */ `
  fragment UpdateSkuM2M on Mutation {
    setProductAttributesForSku(
      input: { skuId: $skuId, attributeIds: $productAttributeIds }
    ) {
      sku {
        id
        productAttributesList(
          filter: { isArchived: { equalTo: false } }
          orderBy: NAME_ASC
        ) {
          ...AttributeSelectOption
        }
      }
    }
    upsertSkuComponentsBySkuIdAndSkuComponentType(
      input: { skuComponents: $skuComponents }
    ) {
      skuComponents {
        id
        ...SelectedSkuSkuComponentFields
      }
      query {
        sku(id: $skuId) {
          ...SelectedSkuSkuComponentsFields
        }
      }
    }
  }
`);

export function resetSkuRelationsCache(cache: ApolloCache<unknown>) {
  const clearNestedListFieldCache = makeClearNestedListFieldCache(cache);
  clearNestedListFieldCache("BrandDistributionCenter", [
    "skuBrandDistributionCenters",
  ]);
  const clearCachedType = makeClearCachedType(cache);
  clearCachedType("PromotionSku", {
    onlyFields: ["skuBrandOrderingPlatform"],
  });
}

const mutationDataFields = gql(/* GraphQL */ `
  fragment MutationDataSkuFields on Sku {
    id
    tiHi {
      id
    }
    skuComponentsList {
      id
      skuComponentType
      dimensions {
        id
      }
    }
  }
`);

export function useGetMutationDataFromFormData(
  skuFragment?: FragmentType<typeof mutationDataFields> | null,
) {
  const sku = getFragmentData(mutationDataFields, skuFragment);

  const validateUpcUniqueness = useValidateUpcUniqueness();

  return async function getMutationDataFromFormData(
    formData: SkuInputs,
    { formContext, disabledComponentTypes }: SubmitContext,
    skuId: UUID,
  ) {
    const {
      skuComponents: skuComponentsRaw,
      productAttributeIds,
      tiHi: tiHiRaw,
      ...baseData
    } = formData;

    const skuComponentsByUpc =
      skuComponentsRaw &&
      keyBy(
        skuComponentsRaw.filter((sc) => sc.upc),
        "upc",
      );

    if (skuComponentsByUpc) {
      const upcs = Object.keys(skuComponentsByUpc);

      const duplicateErrorMsg = "Each UPC for each SKU must be unique.";
      const duplicateUpcsFieldErrors = upcs.reduce((acc, upc) => {
        const newErrors = skuComponentsRaw.reduce((acc2, sc, idx) => {
          if (sc.upc === upc) {
            // eslint-disable-next-line no-param-reassign
            acc2[`skuComponents.${idx}.upc`] = [duplicateErrorMsg];
          }
          return acc2;
        }, {} as ErrorsByFieldName<SkuInputs>);

        if (Object.keys(newErrors).length > 1) {
          return { ...acc, ...newErrors };
        }

        return acc;
      }, {} as ErrorsByFieldName<SkuInputs>);

      if (Object.keys(duplicateUpcsFieldErrors).length) {
        setFieldErrorsWithDict(formContext.setError, duplicateUpcsFieldErrors);
        throw new UserError(duplicateErrorMsg);
      }

      const errorMsg = "UPC is already assigned to another SKU.";
      const validationInputs = upcs.map((upc) => ({
        upc,
        ignoreSkuId: skuId,
      }));
      const upcValidations = await validateUpcUniqueness(validationInputs);
      const fieldErrors = upcValidations.reduce((acc, validation) => {
        if (validation.isUnique) return acc;
        const skuComponent = skuComponentsByUpc[validation.upc];
        assertPresence(skuComponent.skuComponentType);
        const idx = SKU_COMPONENT_TYPES.indexOf(skuComponent.skuComponentType);
        acc[`skuComponents.${idx}.upc`] = [errorMsg];
        return acc;
      }, {} as ErrorsByFieldName<SkuInputs>);
      if (Object.keys(fieldErrors).length) {
        setFieldErrorsWithDict(formContext.setError, fieldErrors);
        throw new UserError(errorMsg);
      }
    }

    const skuComponentsByType = sku?.skuComponentsList
      ? keyBy(sku.skuComponentsList, "skuComponentType")
      : {};

    const skuComponents: SkuComponentInput[] = [];
    const dimensions: DimensionInput[] = [];
    const tiHis: TiHiInput[] = [];

    skuComponentsRaw?.forEach((skuComponent) => {
      if (
        !skuComponent.skuComponentType ||
        disabledComponentTypes.includes(skuComponent.skuComponentType)
      ) {
        return;
      }

      const { dimensions: dimensionsRaw, ...skuComponentAttrs } = skuComponent;

      assertPresence(skuComponentAttrs.skuComponentType);

      const existingSkuComponent =
        skuComponentsByType?.[skuComponentAttrs.skuComponentType];

      const dimensionsWithId = dimensionsRaw
        ? {
            ...dimensionsRaw,
            id: existingSkuComponent?.dimensions?.id || uuidv4(),
          }
        : null;

      if (dimensionsWithId) {
        dimensions.push(dimensionsWithId);
      }

      skuComponents.push({
        ...skuComponentAttrs,
        skuId,
        dimensionsId: dimensionsWithId?.id || null,
      });
    });

    const tiHi = tiHiRaw
      ? {
          ...tiHiRaw,
          id: sku?.tiHi?.id || uuidv4(),
        }
      : null;

    if (tiHi) tiHis.push(tiHi);

    const skuData = {
      ...baseData,
      tiHiId: tiHi?.id || null,
    };

    return {
      skuData,
      skuId,
      productAttributeIds,
      dimensions,
      tiHis,
      skuComponents,
    };
  };
}
